'use client';

import { useSearchParams } from 'next/navigation';
import { StoryblokReactComponent } from './componentsMap';
import { PropsWithChildren, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { MARK_BOLD, render } from 'storyblok-rich-text-react-renderer';
import clsx from 'clsx';
import Image from 'next/image';
import { CapsulaReportStoryblok } from '@/storyblok/types';
import { CapsulaSession } from '@/app/(marketing)/capsula';
import { makeRichTextRenderOptions } from '@/storyblok/utils/richText';
import { monaFont } from '@/styles/fonts';
import { getImageDimensions } from '@/storyblok/utils/asset';

export const StoryblokCapsulaReport: StoryblokReactComponent<CapsulaReportStoryblok> = ({ component, story }) => {
  return (
    <Suspense>
      <Report story={story} component={component} />
    </Suspense>
  );
};

export const Report: StoryblokReactComponent<CapsulaReportStoryblok> = ({ component, story }) => {
  const searchParams = useSearchParams();
  const sessionId = searchParams?.get(component.sessionParamName);

  const [sessionData, setSessionData] = useState<CapsulaSession | undefined>(undefined);
  const [loading, setLoading] = useState(typeof sessionId === 'string');

  useEffect(() => {
    if (!sessionId) return;
    const getSessionsData = async () => {
      try {
        const res = await fetch(`/api/capsula/session?sessionId=${sessionId}`);
        const data = (await res.json()) as CapsulaSession;
        setSessionData(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    getSessionsData();
  }, [sessionId]);

  const bodyComposition = sessionData?.checkupList?.bodyCompositionCheck;
  const age = sessionData?.checkupList?.ageCheck;
  const vascular = sessionData?.checkupList?.vascularCheck;
  const wellbeing = sessionData?.checkupList?.wellbeingHRVCheck;
  const translations = useMemo(
    () =>
      component.scoreTranslations?.tbody.reduce(
        // @ts-ignore - tbd in 86413
        (acc, row) => {
          const key = row.body.at(0);
          const value = row.body.at(1);
          if (!key || !value) return acc;
          return {
            ...acc,
            [key.value ?? '']: value.value ?? '',
          };
        },
        {} as Record<string, string>,
      ),
    [component.scoreTranslations],
  );
  const translate = useCallback((key: string) => translations?.[key] ?? key, [translations]);

  if (!sessionId) return null;

  return (
    <section className="flex max-w-section flex-col gap-7 xl:mx-auto">
      {component.title && loading ? (
        <div className="mx-4 animate-pulse rounded-xl bg-slate-200 md:mx-20 ">
          <span className="text-3xl opacity-0" aria-hidden>
            {render(
              component.title,
              makeRichTextRenderOptions(story, {
                markResolvers: {
                  [MARK_BOLD]: child => <span className={clsx(monaFont.className, 'text-elty-green')}>{child}</span>,
                },
              }),
            )}
          </span>
        </div>
      ) : (
        component.title && (
          <h2 className="mx-auto text-center text-3xl">
            {render(
              component.title,
              makeRichTextRenderOptions(story, {
                markResolvers: {
                  [MARK_BOLD]: child => <span className={clsx(monaFont.className, 'text-elty-green')}>{child}</span>,
                },
              }),
            )}
          </h2>
        )
      )}
      <div className="flex flex-row flex-wrap justify-center gap-x-5 gap-y-7 px-4 md:px-20 lg:flex-nowrap">
        <ReportCard
          loading={loading}
          data={
            bodyComposition && {
              iconFilename: component.bodyCompositionCheckIcon.filename,
              label: component.bodyCompositionCheckLabel,
              date: bodyComposition.creationDate,
            }
          }
        >
          <span className="text-2xl font-medium capitalize text-primary-active">{translate(bodyComposition?.score ?? '')}</span>
        </ReportCard>
        <ReportCard
          loading={loading}
          data={
            age && {
              iconFilename: component.ageCheckIcon.filename,
              label: component.ageCheckLabel,
              date: age.creationDate,
            }
          }
        >
          <span className="text-2xl font-medium capitalize text-primary-active">{translate(age?.score ?? '')}</span>
        </ReportCard>
        <ReportCard
          loading={loading}
          data={
            wellbeing && {
              iconFilename: component.wellbeignCheckIcon.filename,
              label: component.wellbeignCheckLabel,
              date: wellbeing.creationDate,
            }
          }
        >
          <span className="text-2xl font-medium capitalize text-primary-active">{translate(wellbeing?.score ?? '')}</span>
        </ReportCard>
        {vascular?.indicators.bloodPressure?.diastolicPressure && vascular?.indicators.bloodPressure?.systolicPressure && (
          <ReportCard
            loading={loading}
            data={
              vascular && {
                iconFilename: component.vascularCheckIcon.filename,
                label: component.vascularCheckLabel,
                date: vascular.creationDate,
              }
            }
          >
            <p>
              <span className="text-2xl font-medium capitalize text-primary-active">
                {vascular?.indicators.bloodPressure.diastolicPressure.value}
              </span>{' '}
              min
            </p>
            <p>
              <span className="text-2xl font-medium capitalize text-primary-active">
                {vascular?.indicators.bloodPressure.systolicPressure.value}
              </span>{' '}
              max
            </p>
          </ReportCard>
        )}
      </div>
      {loading && (
        <div className="mx-auto animate-pulse rounded-xl bg-slate-200 px-16 py-4">
          <span className="text-lg font-semibold opacity-0" aria-hidden>
            {component.downloadPdfCtaLabel}
          </span>
        </div>
      )}
      {sessionData && (
        <a
          href={`/api/capsula/report?sessionId=${sessionId}`}
          className="mx-auto mt-7 rounded-xl bg-elty-green px-16 py-4 text-center text-lg font-semibold text-white"
          target="_blank"
        >
          {component.downloadPdfCtaLabel}
        </a>
      )}
    </section>
  );
};

const ReportCard = ({
  loading,
  data,
  children,
}: PropsWithChildren<{
  loading: boolean;
  data?: {
    iconFilename: string;
    label: string;
    date: string;
  };
}>) => {
  if (loading) {
    return <div className="h-52 w-[calc(50%-0.675rem)] flex-grow animate-pulse rounded-xl bg-slate-200 shadow lg:w-1/4"></div>;
  }
  if (!data) return null;
  const date = new Date(data.date);
  return (
    <div className="flex w-[calc(50%-0.675rem)] flex-col rounded-xl border bg-neutral-surface p-4 shadow lg:w-1/4">
      <div className="mb-2 flex flex-row items-start justify-between">
        <Image
          className="size-12"
          src={data.iconFilename}
          alt={data.label ?? ''}
          {...getImageDimensions({ filename: data.iconFilename })}
        />
        <span className="text-xs text-neutral-darker">
          {date.getDate()}/{date.getMonth() + 1}/{date.getFullYear()}
        </span>
      </div>
      <span className="mb-3 text-sm font-medium leading-[18px] text-neutral-darker">{data.label}</span>
      <div className="flex min-h-16 flex-col justify-center">{children}</div>
    </div>
  );
};
